import React, { useState, useEffect, useContext, useRef } from "react";
import { Modal } from "react-bootstrap";
import { GlobalContext } from "../Context/GlobalContext";

const HeightModal = ({ show, onClose, onSave, initialHeight, initialUnit }) => {
  const { translatedLabels } = useContext(GlobalContext);
  const authLabel = translatedLabels?.auth;
  const screenLabels = translatedLabels?.common;

  const integerListRef = useRef(null);
  const decimalListRef = useRef(null);

  const [unit, setUnit] = useState(initialUnit || "cm");
  const [integerPart, setIntegerPart] = useState(0);
  const [decimalPart, setDecimalPart] = useState(0);

  const convertHeight = (height, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return height;

    if (fromUnit === "cm" && toUnit === "feet") {
      const totalInches = height / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return parseFloat(`${feet}.${inches}`);
    } else if (fromUnit === "feet" && toUnit === "cm") {
      const [feetPart, inchesPart] = height.toString().split(".");
      const totalInches = parseInt(feetPart) * 12 + parseInt(inchesPart || 0);
      return Math.round(totalInches * 2.54);
    }
    return height;
  };

  useEffect(() => {
    if (show) {
      setUnit(initialUnit || "cm");
    }
  }, [show, initialUnit]);

  useEffect(() => {
    if (show) {
      const height = parseFloat(initialHeight || 0);
      const formattedHeight =
        initialUnit !== unit
          ? convertHeight(height, initialUnit, unit)
          : height;

      if (unit === "cm") {
        setIntegerPart(Math.round(formattedHeight));
        setDecimalPart(0);
      } else {
        const [feetPart, inchesPart] = formattedHeight.toString().split(".");
        setIntegerPart(parseInt(feetPart) || 0);
        setDecimalPart(parseInt(inchesPart || 0));
      }
    }
  }, [show, initialHeight, initialUnit, unit]);

  useEffect(() => {
    if (!show) return;

    const handleScroll = (ref, setValue) => {
      if (!ref.current) return;

      const container = ref.current;
      const items = Array.from(container.querySelectorAll(".picker-item"));
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      let closest = null;
      let closestDistance = Infinity;

      for (let item of items) {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(itemCenter - centerY);

        if (distance < closestDistance) {
          closest = item;
          closestDistance = distance;
        }
      }

      if (closest) {
        const value = parseInt(closest.textContent);
        if (!isNaN(value)) {
          setValue(value);
        }
      }
    };

    const onScrollInteger = () => handleScroll(integerListRef, setIntegerPart);
    const onScrollDecimal = () => handleScroll(decimalListRef, setDecimalPart);

    const integerEl = integerListRef.current;
    const decimalEl = decimalListRef.current;

    integerEl?.addEventListener("scroll", onScrollInteger);
    if (unit === "feet") {
      decimalEl?.addEventListener("scroll", onScrollDecimal);
    }

    onScrollInteger();
    if (unit === "feet") {
      onScrollDecimal();
    }

    return () => {
      integerEl?.removeEventListener("scroll", onScrollInteger);
      if (unit === "feet") {
        decimalEl?.removeEventListener("scroll", onScrollDecimal);
      }
    };
  }, [show, unit]);

  const scrollToValue = (ref, value) => {
    if (!ref.current) return;
    const container = ref.current;
    const item = container.querySelector(
      `.picker-item:nth-child(${value + 2})`
    );
    if (item) {
      const containerHeight = container.offsetHeight;
      const itemHeight = item.offsetHeight;
      container.scrollTop =
        item.offsetTop - containerHeight / 2 + itemHeight / 2;
    }
  };

  useEffect(() => {
    if (show) {
      const height = parseFloat(initialHeight || 0);
      const formattedHeight =
        initialUnit !== unit
          ? convertHeight(height, initialUnit, unit)
          : height;

      let intVal, decVal;
      if (unit === "cm") {
        intVal = Math.round(formattedHeight);
        decVal = 0;
      } else {
        const [feetPart, inchesPart] = formattedHeight.toString().split(".");
        intVal = parseInt(feetPart) || 0;
        decVal = parseInt(inchesPart || 0);
      }

      setIntegerPart(intVal);
      setDecimalPart(decVal);

      setTimeout(() => {
        scrollToValue(integerListRef, intVal);
        if (unit === "feet") {
          scrollToValue(decimalListRef, decVal);
        }
      }, 100);
    }
  }, [show, initialHeight, initialUnit, unit]);

  const handleSave = () => {
    let combinedHeight;
    if (unit === "cm") {
      combinedHeight = integerPart;
    } else {
      combinedHeight = parseFloat(`${integerPart}.${decimalPart}`);
    }
    onSave({ height: combinedHeight, heightUnit: unit });
  };

  const renderScrollPicker = (range, value, setValue, ref) => (
    <div className="picker-wrapper">
      <div className="picker-container" ref={ref}>
        <div className="picker-list">
          <div style={{ height: "40px" }} />
          {range.map((num) => (
            <div
              key={num}
              tabIndex={-1}
              className={`picker-item ${value === num ? "active" : ""}`}
            >
              {num}
            </div>
          ))}
          <div style={{ height: "40px" }} />
        </div>
      </div>
      <div className="picker-border" />
    </div>
  );

  const getIntegerRange = () => {
    return unit === "cm" ? [...Array(273).keys()] : [...Array(9).keys()];
  };

  const getDecimalRange = () => {
    return [...Array(12).keys()];
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {screenLabels?.edit_height?.label_value || "Edit Height"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-center mb-3">
          <div
            className="btn-group w-100 border-primary"
            role="group"
            style={{ borderRadius: "10px", overflow: "hidden" }}
          >
            {["cm", "feet"].map((u) => (
              <React.Fragment key={u}>
                <input
                  type="radio"
                  className="btn-check"
                  name="heightUnit"
                  id={u}
                  checked={unit === u}
                  onChange={() => setUnit(u)}
                />
                <label
                  className={`btn py-2 fw-semibold d-flex align-items-center justify-content-center border-0 ${
                    unit === u ? "bg-primary text-white" : "bg-light"
                  }`}
                  htmlFor={u}
                  style={{ flex: 1 }}
                >
                  {authLabel?.[u]?.label_value || u.toUpperCase()}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-center gap-3 align-items-center position-relative">
          {renderScrollPicker(
            getIntegerRange(),
            integerPart,
            setIntegerPart,
            integerListRef
          )}

          {unit === "feet" && (
            <>
              <div className="fs-3 fw-bold mb-4">.</div>
              {renderScrollPicker(
                getDecimalRange(),
                decimalPart,
                setDecimalPart,
                decimalListRef
              )}
            </>
          )}

          <div className="fs-4 d-flex align-items-center mb-4">
            {unit === "feet" ? "ft" : authLabel?.[unit]?.label_value || unit}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-danger" onClick={onClose}>
          {screenLabels?.cancel?.label_value || "Cancel"}
        </button>
        <button className="btn btn-primary" onClick={handleSave}>
          {screenLabels?.save?.label_value || "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default HeightModal;
